import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import CompanyConfigCard from "./CompanyConfigCard";
import SetCompanyConfigCard from "./SetCompanyConfigCard";
import ServiceRateListCard from "./ServiceRateListCard";
import UserRateForm from "./UserRateForm";
import BulkUpdateForm from "./BulkUpdateForm";
import DeleteRateModal from "./DeleteRateModal";
import ApplicableRatePreview from "./ApplicableRatePreview";
import SearchUser from "./SearchUser";

const SERVICE_TYPES = [
  "AEPS","RECHARGE","PAYOUT","DMT","CREDIT_CARD",
  "PG","PG_DEBIT","PG_UPI","PAYOUT_UPI","CMS",
];

const TABS = [
  { key: "config",  label: "Company Config",  icon: "⚙" },
  { key: "rates",   label: "Rate Management", icon: "◧" },
  { key: "checker", label: "Rate Checker",    icon: "◎" },
];

const RateSet = () => {
  const [serviceType, setServiceType] = useState("AEPS");
  const [activeTab, setActiveTab]     = useState("config");
  const [ratesRefreshKey, setRatesRefreshKey] = useState(0);

  const [showConfigPopup, setShowConfigPopup] = useState(false);
  const [configEditData,  setConfigEditData]  = useState(null);

  const [showRatePopup, setShowRatePopup] = useState(false);
  const [selectedSlab,  setSelectedSlab]  = useState(null);

  const [showBulkPopup, setShowBulkPopup] = useState(false);
  const [bulkUsers,     setBulkUsers]     = useState([]);
  const [bulkSlab,      setBulkSlab]      = useState(null);

  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deleteTarget,    setDeleteTarget]    = useState(null);

  const [checkingUserId,  setCheckingUserId]  = useState(null);
  const [rateCheckBanner, setRateCheckBanner] = useState(null);

  const token = useSelector((s) => s.auth.employeeDetails?.token);

  const refresh = () => setRatesRefreshKey((k) => k + 1);

  const handleEditConfig  = (d)          => { setConfigEditData(d); setShowConfigPopup(true); };
  const handleEditSlab    = (d)          => { setSelectedSlab(d);   setShowRatePopup(true);   };
  const handleBulkEdit    = (users, slab)=> { setBulkUsers(users); setBulkSlab(slab); setShowBulkPopup(true); };
  const handleDeleteRate  = (d)          => { setDeleteTarget(d);   setShowDeletePopup(true); };

  const handleSelectUser = async (user) => {
    const userId   = user.username;
    const userType = user.role;
    setCheckingUserId(userId);
    setRateCheckBanner(null);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/commission/rates/service/${serviceType}/user-type/${userType}/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const rates    = res.data;
      const hasRates = rates && (Array.isArray(rates) ? rates.length > 0 : true);
      if (hasRates) {
        setRateCheckBanner({ type: "found", message: `${userId} already has rates for ${serviceType}. Switching to Rate Management.` });
        setActiveTab("rates");
      } else {
        setSelectedSlab({ userId, userType, serviceType, slab: null });
        setShowRatePopup(true);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 204) {
        setSelectedSlab({ userId, userType, serviceType, slab: null });
        setShowRatePopup(true);
      } else {
        setRateCheckBanner({ type: "error", message: err?.response?.data?.message || `Failed to check rates for ${userId}.` });
      }
    } finally {
      setCheckingUserId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-5 space-y-4">

      {/* ── HEADER ── */}
      <div className="bg-[#0A2A4F] rounded-2xl p-5 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-5">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white tracking-tight">
              Commission Rate System
            </h1>
            <p className="text-blue-300 text-xs mt-0.5">
              Configure caps &rarr; Set slabs &rarr; Verify rates
            </p>
          </div>
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-medium px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {serviceType.replace(/_/g, " ")} selected
          </span>
        </div>

        {/* Service type pills */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {SERVICE_TYPES.map((s) => (
            <button
              key={s}
              onClick={() => setServiceType(s)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                serviceType === s
                  ? "bg-white text-[#0A2A4F] shadow"
                  : "bg-white/10 text-blue-200 hover:bg-white/20 hover:text-white"
              }`}
            >
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>
      </div>

      {/* ── RATE CHECK BANNER ── */}
      {rateCheckBanner && (
        <div className={`flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium ${
          rateCheckBanner.type === "found"
            ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
            : "bg-red-50 border border-red-200 text-red-700"
        }`}>
          <span>{rateCheckBanner.message}</span>
          <button onClick={() => setRateCheckBanner(null)} className="ml-3 opacity-50 hover:opacity-100 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* ── SEARCH USER ── */}
      <SearchUser
        onSelectUser={handleSelectUser}
        checkingUserId={checkingUserId}
        onBulkSelect={(selectedUsers) => {
          const mapped = selectedUsers.map((u) => ({ userId: u.username, userType: u.role }));
          handleBulkEdit(mapped, null);
        }}
      />

      {/* ── TABS ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-1.5 flex gap-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === tab.key
                ? "bg-[#0A2A4F] text-white shadow-sm"
                : "text-gray-400 hover:text-gray-700 hover:bg-gray-50"
            }`}
          >
            <span className="text-base">{tab.icon}</span>
            <span className="hidden sm:block">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── TAB CONTENT ── */}
      {activeTab === "config"  && <CompanyConfigCard serviceType={serviceType} onEdit={handleEditConfig} />}
      {activeTab === "rates"   && (
        <ServiceRateListCard
          serviceType={serviceType}
          refreshKey={ratesRefreshKey}
          onEditSlab={handleEditSlab}
          onBulkEdit={handleBulkEdit}
          onDeleteRate={handleDeleteRate}
        />
      )}
      {activeTab === "checker" && <ApplicableRatePreview serviceType={serviceType} />}

      {/* ── POPUPS ── */}
      {showConfigPopup && (
        <Popup onClose={() => setShowConfigPopup(false)}>
          <SetCompanyConfigCard serviceType={serviceType} editData={configEditData} onClose={() => setShowConfigPopup(false)} />
        </Popup>
      )}

      {showRatePopup && (
        <Popup wide onClose={() => setShowRatePopup(false)}>
          <UserRateForm serviceType={serviceType} editData={selectedSlab}
            onClose={() => { setShowRatePopup(false); refresh(); }} />
        </Popup>
      )}

      {showBulkPopup && (
        <Popup wide onClose={() => setShowBulkPopup(false)}>
          <BulkUpdateForm serviceType={serviceType} users={bulkUsers} slab={bulkSlab}
            onClose={() => { setShowBulkPopup(false); refresh(); }} />
        </Popup>
      )}

      {showDeletePopup && (
        <Popup onClose={() => setShowDeletePopup(false)}>
          <DeleteRateModal serviceType={serviceType} target={deleteTarget}
            onClose={() => { setShowDeletePopup(false); refresh(); }} />
        </Popup>
      )}
    </div>
  );
};

const Popup = ({ children, onClose, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
    <div className={`bg-white rounded-3xl shadow-2xl relative max-h-[92vh] overflow-y-auto w-full ${wide ? "max-w-3xl" : "max-w-lg"}`}>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-800 transition text-sm font-bold"
      >
        ✕
      </button>
      <div className="p-6 md:p-8">{children}</div>
    </div>
  </div>
);

export default RateSet;
